import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, InnerBlocks, InspectorControls } from '@wordpress/block-editor';
import { PanelBody, SelectControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';

registerBlockType('puroclean/section', {
    edit({ attributes, setAttributes }) {
        const { contentWidth, backgroundColor, shadowLevel } = attributes;
        
        const blockProps = useBlockProps({
            className: 'section-container'
        });

        // Get the background class based on the selected color
        const getBackgroundClass = (color) => {
            switch (color) {
                case 'white':
                    return 'bg-white';
                case 'gray-50':
                    return 'bg-gray-50';
                case 'primary':
                    return 'bg-primary';
                case 'secondary':
                    return 'bg-secondary';
                case 'gray-900':
                    return 'bg-gray-900';
                default:
                    return 'bg-transparent';
            }
        };

        // Get the width class based on the selected width
        const getWidthClass = (width) => {
            switch (width) {
                case 'narrow':
                    return 'max-w-narrow';
                case 'inner':
                    return 'max-w-inner';
                case 'content-without-padding':
                    return 'max-w-content-without-padding';
                case 'content':
                default:
                    return 'max-w-content';
            }
        };

        // Get the shadow class based on the selected level
        const getShadowClass = (level) => {
            switch (level) {
                case 'sm':
                    return 'shadow-section-sm';
                case 'md':
                    return 'shadow-section-md';
                case 'lg':
                    return 'shadow-section-lg';
                case 'xl':
                    return 'shadow-section-xl';
                default:
                    return '';
            }
        };

        // Template for inner blocks - allow any blocks
        const ALLOWED_BLOCKS = [
            'core/paragraph',
            'core/heading', 
            'core/image',
            'core/list',
            'core/button',
            'core/quote',
            'core/separator',
            'core/columns',
            'core/column',
            'core/group',
            'core/spacer',
            'puroclean/blog-listing',
            'puroclean/card',
            'puroclean/text-banner',
            'puroclean/flexible-columns',
            'puroclean/cards-group',
            'puroclean/card-content',
            'puroclean/image-slider',
            'puroclean/tabs',
            'puroclean/custom-banner',
            'puroclean/big-card',
            'puroclean/flexible-columns',
            'puroclean/hero-slides'
        ];

        const TEMPLATE = [
            ['core/paragraph', { placeholder: 'Add your content here...' }]
        ];

        const backgroundClass = getBackgroundClass(backgroundColor);
        const widthClass = getWidthClass(contentWidth);
        const shadowClass = getShadowClass(shadowLevel);

        // Determine text color based on background
        const getTextColorClass = (bgColor) => {
            if (bgColor === 'primary' || bgColor === 'secondary' || bgColor === 'gray-900') {
                return 'text-white';
            }
            return 'text-foreground';
        };

        const textColorClass = getTextColorClass(backgroundColor);

        return (
            <>
                <InspectorControls>
                    <PanelBody title={__('Section Settings', 'puroclean')} initialOpen={true}>
                        <SelectControl
                            label={__('Content Width', 'puroclean')}
                            value={contentWidth}
                            options={[
                                { label: __('Narrow (1080px)', 'puroclean'), value: 'narrow' },
                                { label: __('Inner (1364px)', 'puroclean'), value: 'inner' },
                                { label: __('Content Without Padding (1640px)', 'puroclean'), value: 'content-without-padding' },
                                { label: __('Content (1640px)', 'puroclean'), value: 'content' }
                            ]}
                            onChange={(value) => setAttributes({ contentWidth: value })}
                        />
                        
                        <SelectControl
                            label={__('Background Color', 'puroclean')}
                            value={backgroundColor}
                            options={[
                                { label: __('Transparent', 'puroclean'), value: 'transparent' },
                                { label: __('White', 'puroclean'), value: 'white' },
                                { label: __('Light Gray', 'puroclean'), value: 'gray-50' },
                                { label: __('Primary Red', 'puroclean'), value: 'primary' },
                                { label: __('Secondary Blue', 'puroclean'), value: 'secondary' },
                                { label: __('Dark Gray', 'puroclean'), value: 'gray-900' }
                            ]}
                            onChange={(value) => setAttributes({ backgroundColor: value })}
                        />

                        <SelectControl
                            label={__('Shadow', 'puroclean')}
                            value={shadowLevel}
                            options={[
                                { label: __('None', 'puroclean'), value: 'none' },
                                { label: __('Small', 'puroclean'), value: 'sm' },
                                { label: __('Medium', 'puroclean'), value: 'md' },
                                { label: __('Large', 'puroclean'), value: 'lg' },
                                { label: __('Extra Large', 'puroclean'), value: 'xl' }
                            ]}
                            onChange={(value) => setAttributes({ shadowLevel: value })}
                        />
                    </PanelBody>
                </InspectorControls>

                <section {...blockProps}>
                    {/* Editor Preview */}
                    <div className={`py-12 ${backgroundClass} ${shadowClass} ${textColorClass}`}>
                        <div className={`${widthClass} mx-auto`}>
                            <InnerBlocks
                                allowedBlocks={ALLOWED_BLOCKS}
                                template={TEMPLATE}
                                templateLock={false}
                            />
                        </div>
                    </div>
                </section>
            </>
        );
    },
    save() {
        return <InnerBlocks.Content />;
    }
}); 